import { IComponentOptions, IController, IFormController, IOnChangesObjectOf, IPromise, IRootScopeService } from "angular";
import { GtmhubController } from "@gtmhub/core";
import { CommentTargetType, IComment, IGif } from "../../models/comments";
import { CommentService } from "../../services/comment-service";
import { unique } from "@gtmhub/util";
import { IIndicator } from "@gtmhub/error-handling";
import { storage } from "@gtmhub/core/storage";
import { SizeOfGif } from "@gtmhub/shared/services/tenor-gif.service";
import { IAssigneeIdMap, IAssigneesStoreState } from "@gtmhub/assignees";
import { INgRedux } from "@gtmhub/state-management";

export interface IMention {
  id: string;
}

// We don't want the draftComment to survive page refresh
const draftComment = new Map();
let cacheTimestamp = Date.now();
const isCacheTimedOut = (): boolean => {
  const cacheTimeout = 5 * 60 * 1000; // 5 minutes in ms
  return Date.now() - cacheTimestamp > cacheTimeout;
};

const bindStateToScope = (state: IAssigneesStoreState): ICommentEditorRedux => ({
  assignees: state.assignees.map,
});

interface ICommentEditorRedux {
  assignees: IAssigneeIdMap;
}

interface ICommentEditorBindings {
  targetId: string;
  targetType: CommentTargetType;
  targetTitle: string;
  focusOnLoad?: boolean;
  mentions?: {
    removable?: string[];
    nonRemovable?: string[];
  };
  comment?: IComment;
  onCancel?(): void;
  hideTargetMentions?: boolean;
  editIdField?: string;
}

export interface CommentEditorCtrl extends ICommentEditorBindings, ICommentEditorRedux {}

export class CommentEditorCtrl extends GtmhubController implements IController {
  commentForm: IFormController;
  indicators: {
    posting?: IIndicator;
  } = {};

  mentions: {
    removable?: string[];
    nonRemovable?: string[];
  } = {};

  isFocused = false;
  initialCommentText: string;
  initialGifId: string;

  get isNewComment(): boolean {
    return !this.comment?.id;
  }

  get cacheId(): string {
    return this.isNewComment ? this.targetId : `${this.targetId}:${this.comment.id}`;
  }

  readonly sizeOfGif = SizeOfGif;

  static $inject = ["$rootScope", "CommentService", "$ngRedux"];

  constructor(private $rootScope: IRootScopeService, private commentService: CommentService, private $ngRedux: INgRedux) {
    super();
  }

  $onInit(): void {
    this.onDestroy(this.$ngRedux.connect(bindStateToScope)(this));

    if (!this.comment) {
      this.resetContent();
    }

    if (!isCacheTimedOut() && draftComment[this.cacheId]) {
      this.comment = draftComment[this.cacheId];
    }
  }

  $onChanges(changes: IOnChangesObjectOf<ICommentEditorBindings>): void {
    if (changes.comment?.currentValue) {
      this.comment = angular.copy(changes.comment.currentValue);
      this.initialCommentText = this.comment.text;
      this.initialGifId = this.comment.gif?.id || "";
    }
  }

  $onDestroy(): void {
    const modalCloseReason = storage.get("modalCloseReason");
    if (modalCloseReason && String(modalCloseReason).includes("backdrop")) {
      cacheTimestamp = Date.now();
      draftComment[this.cacheId] = this.comment;
    } else {
      draftComment[this.cacheId] = null;
    }
  }

  commentMentionsChanged(mentions: IMention[] | string[]): void {
    // It is called on change event, but that means it will be called,
    // even if whitespace is deleted from the input.
    if (mentions.length !== this.mentions.nonRemovable?.length) {
      this.mentions.nonRemovable = mentions.map((mention) => mention.id || mention);
    }
  }

  setCommentGif = (gif: IGif): void => {
    this.comment.gif = gif;
  };

  createComment(): void {
    if (this.commentForm.$invalid) {
      return;
    }

    const mentioned = unique([...(this.mentions.removable || []), ...(this.mentions.nonRemovable || [])]);
    const newComment: IComment = {
      text: this.comment.text,
      gif: this.comment.gif,
      targetId: this.targetId,
      targetType: this.targetType,
      targetTitle: this.targetTitle,
      mentioned,
    };

    this.handleCommentsPromise(this.commentService.post(newComment));
  }

  editComment(): void {
    if (this.commentForm.$invalid) {
      return;
    }

    const currentText = this.comment.text;
    const currentGifId = this.comment.gif?.id || "";
    const hasContentChange = this.initialCommentText !== currentText || this.initialGifId !== currentGifId;

    if (hasContentChange) {
      const commentContent: Partial<IComment> = {
        text: this.comment.text,
        gif: this.comment.gif,
        mentioned: this.comment.mentioned,
      };

      const gifChanges = {
        isAdded: currentGifId && !this.initialGifId,
        isDeleted: this.initialGifId && !currentGifId,
      };

      // feed comments are duplicated as array records in each FeedEntry document with new IDs
      // the original ID of the new record is stored in its targetId field =>
      // so providing the option to pass a different ID field than the default `id` for the PUT request
      const commentId = this.comment[this.editIdField] || this.comment.id;
      this.handleCommentsPromise(this.commentService.edit(commentId, commentContent, gifChanges));
    } else {
      this.onCancel();
    }
  }

  publish(): void {
    if (this.isNewComment) {
      this.createComment();
    } else {
      this.editComment();
    }
  }

  removeMention(id: string): void {
    this.mentions.removable = this.mentions.removable.filter((mention) => mention !== id);
  }

  public resetIndicator(indicator: keyof CommentEditorCtrl["indicators"]): void {
    delete this.indicators[indicator];
  }

  private resetContent(): void {
    this.comment = {
      text: "",
      gif: null,
    } as IComment;
  }

  private handleCommentsPromise(promise: IPromise<IComment>) {
    this.indicators.posting = { progress: true };

    promise
      .then((comment) => {
        this.resetIndicator("posting");

        if (this.isNewComment) {
          this.resetContent();
          this.$rootScope.$broadcast("commentPosted", comment);
        } else {
          this.$rootScope.$broadcast("commentEdited", comment);
        }
      })
      .catch((error) => (this.indicators.posting = { error }));
  }
}

export const CommentEditorComponent: IComponentOptions = {
  bindings: {
    targetId: "<",
    targetType: "<",
    targetTitle: "<",
    mentions: "<?",
    focusOnLoad: "<?",
    comment: "<?",
    onCancel: "&",
    hideTargetMentions: "<?",
    editIdField: "<?",
  },
  controller: CommentEditorCtrl,
  templateUrl: require("./comment-editor.component.jade"),
};
